import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { EyeOff, Eye, Save } from "lucide-react";

interface OfferVisibility {
  id: number;
  name: string;
  visibility: string;
}

export function AffiliateVisibilityTab({ affiliateId }: { affiliateId: string }) {
  const queryClient = useQueryClient();
  const [changes, setChanges] = useState<Record<number, string>>({});

  const { data: offersData, isLoading } = useQuery({
    queryKey: ["offers", "list"],
    queryFn: async () => {
      const res = await api.get("/v1/offers?limit=500");
      if (!res.ok) throw new Error("Failed to fetch offers");
      return res.json();
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async (payload: { visibility: string; offerIds: number[] }) => {
      const res = await api.patch(`/v1/offers/visibility/affiliate/${affiliateId}`, payload);
      if (!res.ok) throw new Error("Failed to update visibility");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers"] });
      setChanges({});
      alert("Visibility updated!");
    },
  });

  const offers: OfferVisibility[] = (offersData?.data ?? []).map((o: any) => ({
    id: o.id,
    name: o.name,
    visibility: o.visibility || "public",
  }));

  const pendingCount = Object.keys(changes).length;

  function handleVisibilityChange(offerId: number, visibility: string) {
    setChanges((prev) => ({ ...prev, [offerId]: visibility }));
  }

  function handleBulkSave(visibility: string) {
    const offerIds = Object.entries(changes)
      .filter(([_, v]) => v === visibility)
      .map(([id]) => Number(id));
    if (offerIds.length === 0) return;
    bulkMutation.mutate({ visibility, offerIds });
  }

  function handleSaveAll() {
    const grouped: Record<string, number[]> = {};
    for (const [id, vis] of Object.entries(changes)) {
      if (!grouped[vis]) grouped[vis] = [];
      grouped[vis].push(Number(id));
    }
    for (const [visibility, offerIds] of Object.entries(grouped)) {
      bulkMutation.mutate({ visibility, offerIds });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">
          Manage which offers this affiliate can see
        </h3>
        {pendingCount > 0 && (
          <button
            onClick={handleSaveAll}
            disabled={bulkMutation.isPending}
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Save {pendingCount} change{pendingCount !== 1 ? "s" : ""}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : offers.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <EyeOff className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No offers found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Offer</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Current Visibility</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">New Visibility</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer) => {
                const currentVis = changes[offer.id] ?? offer.visibility;
                const isChanged = changes[offer.id] !== undefined;
                return (
                  <tr key={offer.id} className={`border-b border-gray-100 ${isChanged ? "bg-yellow-50" : "hover:bg-gray-50"}`}>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{offer.name}</span>
                      <span className="text-xs text-gray-400 ml-2">#{offer.id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                        offer.visibility === "public" ? "bg-green-50 text-green-700" :
                        offer.visibility === "private" ? "bg-red-50 text-red-700" :
                        "bg-yellow-50 text-yellow-700"
                      }`}>
                        {offer.visibility === "public" ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {offer.visibility}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={currentVis}
                        onChange={(e) => handleVisibilityChange(offer.id, e.target.value)}
                        className={`text-xs rounded-lg border px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-900 ${isChanged ? "border-yellow-400 bg-yellow-50" : "border-gray-300"}`}
                      >
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                        <option value="require_approval">Require Approval</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
